import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  Users, 
  DollarSign, 
  Calendar,
  Target,
  Activity,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Eye,
  Edit,
  Plus,
  Filter,
  Download,
  RefreshCw,
  X,
  Search,
  Settings,
  Timer,
  Calendar as CalendarIcon,
  DollarSign as DollarIcon,
  UserCheck
} from 'lucide-react';
import { useToast } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const TimeTrackingDashboardReport = () => {
  const { success, error: showError } = useToast();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState({
    // KPI Cards
    totalHours: 0,
    billableHours: 0,
    nonBillableHours: 0,
    totalRevenue: 0,
    averageProductivity: 0,
    activeUsers: 0,
    
    // Time Distribution
    timeDistribution: {
      development: 0,
      design: 0,
      testing: 0,
      meetings: 0,
      admin: 0
    },
    
    // Project Time Allocation
    projectAllocation: [],
    
    // Team Performance
    teamPerformance: [],
    
    // Weekly Trends
    weeklyTrends: [],
    
    // Recent Activities
    recentActivities: [],
    
    // Top Performers
    topPerformers: [],
    
    // Time Tracking Health
    trackingHealth: {
      compliant: 0,
      atRisk: 0,
      nonCompliant: 0
    },
    
    // Overtime Analysis
    overtimeAnalysis: {
      totalOvertime: 0,
      averageOvertime: 0,
      overtimeProjects: []
    }
  });

  // Filter states
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    project: '',
    team: '',
    status: '',
    priority: '',
    manager: ''
  });

  const [searchTerm, setSearchTerm] = useState('');

  // Load report data
  useEffect(() => {
    loadReportData();
  }, []);

  const loadReportData = async () => {
    setLoading(true);
    try {
      // Mock data for demonstration
      const mockData = {
        totalHours: 2840.5,
        billableHours: 2130.2,
        nonBillableHours: 710.3,
        totalRevenue: 159750.00,
        averageProductivity: 87.5,
        activeUsers: 24,
        
        timeDistribution: {
          development: 45,
          design: 20,
          testing: 15,
          meetings: 12,
          admin: 8
        },
        
        projectAllocation: [
          { name: 'Digital Transformation', hours: 450, percentage: 25 },
          { name: 'Infrastructure Modernization', hours: 380, percentage: 21 },
          { name: 'Customer Experience', hours: 320, percentage: 18 },
          { name: 'Data Analytics', hours: 280, percentage: 16 },
          { name: 'Mobile App Development', hours: 220, percentage: 12 },
          { name: 'Other Projects', hours: 190, percentage: 8 }
        ],
        
        teamPerformance: [
          { name: 'Sarah Johnson', hours: 42, productivity: 95, billable: 38 },
          { name: 'Mike Chen', hours: 40, productivity: 92, billable: 36 },
          { name: 'Lisa Wang', hours: 38, productivity: 88, billable: 34 },
          { name: 'David Smith', hours: 41, productivity: 85, billable: 35 },
          { name: 'Emma Davis', hours: 39, productivity: 90, billable: 37 }
        ],
        
        weeklyTrends: [
          { week: 'Week 1', hours: 35, billable: 28, revenue: 2100 },
          { week: 'Week 2', hours: 42, billable: 32, revenue: 2400 },
          { week: 'Week 3', hours: 38, billable: 30, revenue: 2250 },
          { week: 'Week 4', hours: 45, billable: 35, revenue: 2625 },
          { week: 'Week 5', hours: 40, billable: 32, revenue: 2400 }
        ],
        
        recentActivities: [
          { id: 1, user: 'Sarah Johnson', action: 'Logged 8 hours', project: 'Digital Transformation', time: '2 hours ago' },
          { id: 2, user: 'Mike Chen', action: 'Submitted timesheet', project: 'Infrastructure Modernization', time: '3 hours ago' },
          { id: 3, user: 'Lisa Wang', action: 'Started timer', project: 'Customer Experience', time: '4 hours ago' },
          { id: 4, user: 'David Smith', action: 'Approved timesheet', project: 'Data Analytics', time: '5 hours ago' },
          { id: 5, user: 'Emma Davis', action: 'Logged 6 hours', project: 'Mobile App Development', time: '6 hours ago' }
        ],
        
        topPerformers: [
          { name: 'Sarah Johnson', hours: 42, productivity: 95, revenue: 3150 },
          { name: 'Mike Chen', hours: 40, productivity: 92, revenue: 3000 },
          { name: 'Lisa Wang', hours: 38, productivity: 88, revenue: 2850 }
        ],
        
        trackingHealth: {
          compliant: 18,
          atRisk: 4,
          nonCompliant: 2
        },
        
        overtimeAnalysis: {
          totalOvertime: 45.5,
          averageOvertime: 2.3,
          overtimeProjects: ['Digital Transformation', 'Infrastructure Modernization']
        }
      };
      
      setReportData(mockData);
      success('Time tracking dashboard data loaded successfully!');
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      showError(err.message || 'Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleExport = () => {
    // Simulate export functionality
    success('Dashboard report exported successfully!');
  };

  const handleRefresh = () => {
    loadReportData();
  };

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Time Tracking Dashboard Reports"
        description="Comprehensive overview of time tracking activities and performance metrics"
      />
      <div className="page-container">
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginBottom: '24px' }}>
          <button 
            className="btn-outline-cam"
            onClick={handleExport}
          >
            <Download size={16} />
            Export PDF
          </button>
          <button 
            className="btn-cam"
            onClick={handleExport}
          >
            <Download size={16} />
            Export Excel
          </button>
        </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-2">
              <label className="form-label">From Date *</label>
              <div className="position-relative">
                <input
                  type="date"
                  className="form-control"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  placeholder="dd/mm/yyyy"
                />
                <Calendar size={16} className="position-absolute" style={{ right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
              </div>
            </div>
            <div className="col-md-2">
              <label className="form-label">To Date *</label>
              <div className="position-relative">
                <input
                  type="date"
                  className="form-control"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  placeholder="dd/mm/yyyy"
                />
                <Calendar size={16} className="position-absolute" style={{ right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
              </div>
            </div>
            <div className="col-md-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="on-hold">On Hold</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Priority</label>
              <select
                className="form-select"
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
              >
                <option value="">All Priority</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Project Manager</label>
              <select
                className="form-select"
                value={filters.manager}
                onChange={(e) => handleFilterChange('manager', e.target.value)}
              >
                <option value="">All Managers</option>
                <option value="sarah-johnson">Sarah Johnson</option>
                <option value="mike-chen">Mike Chen</option>
                <option value="lisa-wang">Lisa Wang</option>
                <option value="david-smith">David Smith</option>
              </select>
            </div>
            <div className="col-md-2 d-flex align-items-end gap-2">
              <button className="btn btn-primary flex-fill">
                Apply Filter
              </button>
              <button className="btn btn-outline-secondary flex-fill">
                Reset Filter
              </button>
            </div>
          </div>
          
          {/* Search Bar */}
          <div className="row">
            <div className="col-md-12">
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search projects, milestones, or activities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: '40px' }}
                />
                <Search size={16} className="position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="row mb-4">
        <div className="col-md-2-4 mb-3">
          <div className="card h-100">
            <div className="card-body text-center">
              <div className="mb-3">
                <BarChart3 size={32} className="text-primary" />
              </div>
              <h3 className="mb-1 font-weight-bold">{reportData.totalHours.toFixed(0)}</h3>
              <p className="text-muted mb-0">Total Hours</p>
            </div>
          </div>
        </div>
        <div className="col-md-2-4 mb-3">
          <div className="card h-100">
            <div className="card-body text-center">
              <div className="mb-3">
                <Activity size={32} className="text-success" />
              </div>
              <h3 className="mb-1 font-weight-bold">{reportData.billableHours.toFixed(0)}</h3>
              <p className="text-muted mb-0">Active Hours</p>
            </div>
          </div>
        </div>
        <div className="col-md-2-4 mb-3">
          <div className="card h-100">
            <div className="card-body text-center">
              <div className="mb-3">
                <CheckCircle size={32} className="text-info" />
              </div>
              <h3 className="mb-1 font-weight-bold">{Math.floor(reportData.totalHours * 0.3)}</h3>
              <p className="text-muted mb-0">Completed</p>
            </div>
          </div>
        </div>
        <div className="col-md-2-4 mb-3">
          <div className="card h-100">
            <div className="card-body text-center">
              <div className="mb-3">
                <DollarSign size={32} className="text-warning" />
              </div>
              <h3 className="mb-1 font-weight-bold">${(reportData.totalRevenue / 1000).toFixed(0)}K</h3>
              <p className="text-muted mb-0">Total Revenue</p>
            </div>
          </div>
        </div>
        <div className="col-md-2-4 mb-3">
          <div className="card h-100">
            <div className="card-body text-center">
              <div className="mb-3">
                <TrendingUp size={32} className="text-danger" />
              </div>
              <h3 className="mb-1 font-weight-bold">{reportData.averageProductivity}%</h3>
              <p className="text-muted mb-0">Avg Progress</p>
            </div>
          </div>
        </div>
      </div>

      {/* Time Tracking Data Table */}
      <div className="card">
        <div className="card-header" style={{ 
          background: 'var(--primary-color)', 
          color: 'white',
          borderRadius: '8px 8px 0 0'
        }}>
          <h5 className="card-title mb-0 text-white">Time Tracking Overview</h5>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Project Name</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Manager</th>
                  <th>Budget</th>
                  <th>Actual</th>
                  <th>Variance</th>
                  <th>Progress</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Health</th>
                  <th>Risks</th>
                  <th>Milestones</th>
                  <th>Team Size</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div>
                      <div className="fw-bold">Digital Transformation</div>
                      <small className="text-muted">PRJ-001</small>
                    </div>
                  </td>
                  <td>
                    <span className="badge bg-success">On Track</span>
                  </td>
                  <td>
                    <span className="badge bg-light text-dark">Low</span>
                  </td>
                  <td>Sarah Johnson</td>
                  <td>$500,000</td>
                  <td>$450,000</td>
                  <td className="text-success">$50,000 Under</td>
                  <td>
                    <div className="d-flex align-items-center">
                      <div className="progress me-2" style={{ width: '60px', height: '8px' }}>
                        <div className="progress-bar bg-success" style={{ width: '75%' }}></div>
                      </div>
                      <span className="fw-bold">75%</span>
                    </div>
                  </td>
                  <td>15/1/2024</td>
                  <td>30/6/2024</td>
                  <td>
                    <span className="badge bg-warning">Fair</span>
                  </td>
                  <td>2</td>
                  <td>8</td>
                  <td>5</td>
                  <td>
                    <div className="d-flex gap-1">
                      <button className="btn btn-sm btn-outline-primary">
                        <Eye size={14} />
                      </button>
                      <button className="btn btn-sm btn-outline-secondary">
                        <Edit size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <div>
                      <div className="fw-bold">Infrastructure Modernization</div>
                      <small className="text-muted">PRJ-002</small>
                    </div>
                  </td>
                  <td>
                    <span className="badge bg-warning">At Risk</span>
                  </td>
                  <td>
                    <span className="badge bg-danger">High</span>
                  </td>
                  <td>Mike Chen</td>
                  <td>$300,000</td>
                  <td>$320,000</td>
                  <td className="text-danger">$20,000 Over</td>
                  <td>
                    <div className="d-flex align-items-center">
                      <div className="progress me-2" style={{ width: '60px', height: '8px' }}>
                        <div className="progress-bar bg-warning" style={{ width: '60%' }}></div>
                      </div>
                      <span className="fw-bold">60%</span>
                    </div>
                  </td>
                  <td>1/2/2024</td>
                  <td>31/7/2024</td>
                  <td>
                    <span className="badge bg-danger">Poor</span>
                  </td>
                  <td>5</td>
                  <td>12</td>
                  <td>8</td>
                  <td>
                    <div className="d-flex gap-1">
                      <button className="btn btn-sm btn-outline-primary">
                        <Eye size={14} />
                      </button>
                      <button className="btn btn-sm btn-outline-secondary">
                        <Edit size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <div>
                      <div className="fw-bold">Customer Experience</div>
                      <small className="text-muted">PRJ-003</small>
                    </div>
                  </td>
                  <td>
                    <span className="badge bg-success">On Track</span>
                  </td>
                  <td>
                    <span className="badge bg-warning">Medium</span>
                  </td>
                  <td>Lisa Wang</td>
                  <td>$200,000</td>
                  <td>$180,000</td>
                  <td className="text-success">$20,000 Under</td>
                  <td>
                    <div className="d-flex align-items-center">
                      <div className="progress me-2" style={{ width: '60px', height: '8px' }}>
                        <div className="progress-bar bg-success" style={{ width: '90%' }}></div>
                      </div>
                      <span className="fw-bold">90%</span>
                    </div>
                  </td>
                  <td>10/3/2024</td>
                  <td>15/8/2024</td>
                  <td>
                    <span className="badge bg-success">Good</span>
                  </td>
                  <td>1</td>
                  <td>6</td>
                  <td>4</td>
                  <td>
                    <div className="d-flex gap-1">
                      <button className="btn btn-sm btn-outline-primary">
                        <Eye size={14} />
                      </button>
                      <button className="btn btn-sm btn-outline-secondary">
                        <Edit size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Summary Row */}
      <div className="row mt-3">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="row text-center">
                <div className="col-md-4">
                  <h5 className="mb-1">Total Budget Value</h5>
                  <h4 className="text-primary mb-0">₹ 25,00,000</h4>
                </div>
                <div className="col-md-4">
                  <h5 className="mb-1">Total Spent Value</h5>
                  <h4 className="text-warning mb-0">₹ 18,00,000</h4>
                </div>
                <div className="col-md-4">
                  <h5 className="mb-1">Total Variance</h5>
                  <h4 className="text-success mb-0">₹ -7,00,000</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default TimeTrackingDashboardReport;
